import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { UTILS } from "utils";
import AppTabs from "components/ui/AppTabs";
import AppSelect from "components/ui/AppSelect";
import { useMatchMediaDimensions } from "hooks/useWindowDimensions";

const tabsData=[
  {
    label: "All Orders",
    value:"ALL",
    activeClassName: "bg-primary text-white shadow-sm",
    inactiveClassName: "text-gray-600",
    hoverClassName: "hover:bg-primary hover:text-white"
  },
  {
    label: "Pending",
    // "Pending" is also a payment-link status, so this tab gets its own translation key
    i18nKey: "Pending Orders",
    value:'PENDING',
    activeClassName: "bg-yellow-100 text-yellow-700 shadow-sm",
    inactiveClassName: "text-yellow-600",
    hoverClassName: "hover:bg-yellow-50 hover:text-yellow-700"
  },
  {
    label: "Preparing",
    value:"PREPARING",
    activeClassName: "bg-purple-100 text-purple-600 shadow-sm",
    inactiveClassName: "text-purple-500",
    hoverClassName: "hover:bg-purple-50 hover:text-purple-600"
  },
  {
    label: "On The Way / Ready",
    value:"ON_THE_WAY",
    activeClassName: "bg-blue-100 text-blue-700 shadow-sm",
    inactiveClassName: "text-blue-600",
    hoverClassName: "hover:bg-blue-50 hover:text-blue-700"
  },
  {
    label: "Scheduled",
    value:"SCHEDULED",
    activeClassName: "bg-green-100 text-green-700 shadow-sm",
    inactiveClassName: "text-green-600",
    hoverClassName: "hover:bg-green-50 hover:text-green-700"
  },
  {
    label: "Order Refunds",
    value:"REFUND",
    activeClassName: "bg-red-100 text-red-700 shadow-sm",
    inactiveClassName: "text-red-600",
    hoverClassName: "hover:bg-red-50 hover:text-red-700"
  }

]

const ORDER_STATUS = {
  ALL: "ALL",
  IN_PROGRESS: "IN_PROGRESS",
  PENDING: "PENDING",
  PREPARING: "PREPARING",
  ON_THE_WAY: "ON_THE_WAY",
  COMPLETE: "COMPLETE",
  CANCELED: "CANCELLED",
  REJECTED: "REJECTED",
  SCHEDULED: "SCHEDULED",
  REFUND: "REFUND",
};

const OrderTabs = (props) => {

const isMatch1300 = useMatchMediaDimensions(1300)
  const [activeTab, setActiveTab] = useState("PENDING");
  
  const { t } = useTranslation();

  const handleTabClick = (value) => {
    setActiveTab(value);
    props.handleTabStatus(value)
  };


  const getPendingOrders = (status) => {
    if (
      UTILS.isValidObject(["pendingOrdersCount"], props.orderCountData) &&
      Array.isArray(props.orderCountData.pendingOrdersCount)
    ) {
      for (
        let index = 0;
        index < props.orderCountData.pendingOrdersCount.length;
        index++
      ) {
        const element = props.orderCountData.pendingOrdersCount[index];
        if (
          UTILS.isValidObject(["orderStatus"], element) &&
          element.orderStatus === status
        ) {
          return element.count;
        }
      }
      return 0;
    }
  };

  const getRefundableOrders = () => {
    if (
      props.refundableOrdersCountData?.orders?.refundableOrdersCount &&
      Array.isArray(
        props.refundableOrdersCountData?.orders?.refundableOrdersCount
      )
    ) {
      return props.refundableOrdersCountData?.orders?.refundableOrdersCount?.reduce(
        (acc, statusCount) => {
          return statusCount.count + acc;
        },
        0
      );
    }
    return "0";
  };

  const getOrderCount = (tabValue) => {
    const parseCount = (count) => {
      if (count === undefined || count === null) return 0;
      const num = typeof count === 'string' ? parseInt(count, 10) : count;
      return isNaN(num) ? 0 : num;
    };

    switch (tabValue) {
      case "ALL":
        return (
          parseCount(getPendingOrders(ORDER_STATUS.IN_PROGRESS)) +
          parseCount(getPendingOrders(ORDER_STATUS.PENDING)) +
          parseCount(getPendingOrders(ORDER_STATUS.PREPARING)) +
          parseCount(getPendingOrders(ORDER_STATUS.ON_THE_WAY)) +
          parseCount(getPendingOrders(ORDER_STATUS.SCHEDULED)) +
          parseCount(getPendingOrders(ORDER_STATUS.COMPLETE)) +
          parseCount(getPendingOrders(ORDER_STATUS.CANCELED)) +
          parseCount(getPendingOrders(ORDER_STATUS.REJECTED)) +
          parseCount(getRefundableOrders())
        );
      case "IN_PROGRESS": {
        const inProgressCount = parseCount(
          getPendingOrders(ORDER_STATUS.IN_PROGRESS)
        );
        if (inProgressCount > 0) return inProgressCount;
        return (
          parseCount(getPendingOrders(ORDER_STATUS.PREPARING)) +
          parseCount(getPendingOrders(ORDER_STATUS.ON_THE_WAY))
        );
      }
      case "PENDING":
        return parseCount(getPendingOrders(ORDER_STATUS.PENDING));
      case "PREPARING":
        return parseCount(getPendingOrders(ORDER_STATUS.PREPARING));
      case "ON_THE_WAY":
        return parseCount(getPendingOrders(ORDER_STATUS.ON_THE_WAY));
      case "SCHEDULED":
        return parseCount(getPendingOrders(ORDER_STATUS.SCHEDULED));
      case "REFUND":
        return parseCount(getRefundableOrders());
      default:
        return 0;
    }
  };

  // Generate tabs with counts in labels
  const tabsWithCounts = tabsData.map((tab) => {
    const count = getOrderCount(tab.value);
    return {
      ...tab,
      label: `${t(tab.i18nKey || tab.label, tab.label)} (${count})`,
    };
  });

  const orderStatusOptions = tabsWithCounts.map((tab) => ({
    value: tab.value,
    label: tab.label,
  }));

  const TabButtons = ({ activeTab }) => {
    return (
      <>
      <AppTabs tabs={tabsWithCounts} value={activeTab} onChange={(value)=>handleTabClick(value)}/>
      </>
    );
  };

  TabButtons.propTypes = {
    activeTab: PropTypes.string,
  };
  return (
    <>
     {isMatch1300 ? 
     <div 
     style={{width: "12rem"}}>
        <AppSelect
          label={t("Order status")}
          options={orderStatusOptions}
          value={activeTab}
          onChange={handleTabClick}
          disabled={props.loading}
          className="order-tabs-order-status-select"
        />
        </div> :  <TabButtons activeTab={activeTab} />}
    </>
  );
};

OrderTabs.propTypes = {
  orderCountData: PropTypes.object,
  handleTabStatus: PropTypes.func,
  loading: PropTypes.bool,
};

export default OrderTabs;
